import { DateTime } from 'luxon';
import {
    DicomPatientSexValues,
    PATIENT_DATE_OF_BIRTH_FORMAT,
    type PatientPersonalInfo,
    type DicomPatientSex,
    type DicomPatientName,
} from './types';
import { type DicomInfo } from '@/lib/dicom/DicomInfo';
import { formatPatientName, type PatientName } from '@/domain/case/personName';

export enum PatientComparisonErrorType {
    Name = 'name',
    DateOfBirth = 'date-of-birth',
    Sex = 'sex',
}

export interface PatientComparisonError {
    type: PatientComparisonErrorType;
    values: unknown[];
}

export interface PatientComparison {
    isSame: boolean;
    patient: PatientPersonalInfo;
    anotherPatient: PatientPersonalInfo;
    errorFields?: PatientComparisonError[];
}

export class PatientComparisonUtil {
    /** Maps the patient details in a dicom file to the client/api {@link PatientPersonalInfo} */
    public static fromDicom(dicomInfo: DicomInfo): PatientPersonalInfo {
        const mapPatientName = (info: DicomInfo): PatientName | null => {
            return _patientNamefromDicom(info.patientName ?? null);
        };

        const mapPatientSex = (info: DicomInfo): DicomPatientSex | null => {
            switch (info.patientSex) {
                case DicomPatientSexValues.Male:
                    return 'male';

                case DicomPatientSexValues.Female:
                    return 'female';

                case DicomPatientSexValues.Other:
                    return 'other';

                default:
                    return null;
            }
        };

        const mapPatientDate = (info: DicomInfo): string | null => {
            if (info.patientBirthDate) {
                return (
                    DateTime.fromJSDate(info.patientBirthDate).toFormat(
                        PATIENT_DATE_OF_BIRTH_FORMAT,
                    ) ?? null
                );
            }

            return null;
        };

        return {
            name: mapPatientName(dicomInfo) ?? undefined,
            sex: mapPatientSex(dicomInfo) ?? undefined,
            birth_date: mapPatientDate(dicomInfo) ?? undefined,
        };
    }

    /**
     * Compare two different patient's metadata.
     *
     * Note: The underlying checks are case insensitive string comparisons.
     */
    public static compare(
        patient: PatientPersonalInfo,
        anotherPatient: PatientPersonalInfo,
    ): PatientComparison {
        const compareEqual = (str1: string | undefined, str2: string | undefined) =>
            (str1 ?? '').trim().toLowerCase() === (str2 ?? '').trim().toLowerCase();
        const compareName = (): void => {
            if (
                compareEqual(patient.name?.family, anotherPatient.name?.family) &&
                compareEqual(patient.name?.given, anotherPatient.name?.given) &&
                compareEqual(patient.name?.middle, anotherPatient.name?.middle)
            ) {
                // nothing to do
            } else {
                errors.push({
                    type: PatientComparisonErrorType.Name,
                    values: [
                        formatPatientName(patient.name),
                        formatPatientName(anotherPatient.name),
                    ],
                });
            }
        };
        const compareBirthDate = (): void => {
            if (compareEqual(patient.birth_date, anotherPatient.birth_date)) {
                // nothing to do
            } else {
                errors.push({
                    type: PatientComparisonErrorType.DateOfBirth,
                    values: [patient.birth_date, anotherPatient.birth_date],
                });
            }
        };
        const compareSex = (): void => {
            if (compareEqual(patient.sex, anotherPatient.sex)) {
                // nothing to do
            } else {
                errors.push({
                    type: PatientComparisonErrorType.Sex,
                    values: [patient.sex, anotherPatient.sex],
                });
            }
        };

        const errors: PatientComparisonError[] = [];

        compareName();
        compareBirthDate();
        compareSex();

        return {
            isSame: errors.length === 0,
            patient,
            anotherPatient,
            errorFields: errors,
        };
    }
}

/**
 * Cast a {@link DicomPatientName} to a {@link PatientName}
 */
function _patientNamefromDicom(name?: DicomPatientName | null): PatientName | null {
    if (name) {
        return {
            title: name.prefix,
            family: name.familyName,
            given: name.givenName,
            middle: name.middleName,
        };
    }

    return null;
}
